import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getBlogPostBySlug } from '../lib/blogPosts';
import usePageMeta from '../hooks/usePageMeta';
import { getDashboardOverview } from '../services/api';
import '../styles/blog.css';

const FALLBACK_RISQLY_PROFILE_URL = 'https://risqly.net/company/example%20company';

function formatPublishedDate(dateString) {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
}

function buildRisqlyProfileUrl(normalizedName) {
  const safeName = String(normalizedName || '').trim();
  if (!safeName) return FALLBACK_RISQLY_PROFILE_URL;
  return `https://risqly.net/company/${encodeURIComponent(safeName)}`;
}

function injectProfileUrl(text, profileUrl) {
  return String(text || '').replaceAll('{{RISQLY_PROFILE_URL}}', profileUrl);
}

function BlogPost() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth0();
  const post = getBlogPostBySlug(slug || '');
  const [copiedSection, setCopiedSection] = useState('');
  const [risqlyProfileUrl, setRisqlyProfileUrl] = useState(FALLBACK_RISQLY_PROFILE_URL);

  usePageMeta({
    title: post ? post.title : 'Blog',
    description: post?.excerpt || 'Insights from Risqly on vendor cybersecurity risk and resilience.',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRisqlyProfileUrl(FALLBACK_RISQLY_PROFILE_URL);
      return undefined;
    }

    let isActive = true;

    const loadCompanyUrl = async () => {
      try {
        const overview = await getDashboardOverview();
        if (isActive) {
          setRisqlyProfileUrl(buildRisqlyProfileUrl(overview?.normalized_name));
        }
      } catch {
        if (isActive) {
          setRisqlyProfileUrl(FALLBACK_RISQLY_PROFILE_URL);
        }
      }
    };

    loadCompanyUrl();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const handleCopyPrompt = async (section) => {
    if (!section.aiPrompt) return;

    try {
      await copyTextToClipboard(injectProfileUrl(section.aiPrompt, risqlyProfileUrl));
      setCopiedSection(section.heading);
      window.setTimeout(() => setCopiedSection(''), 1800);
    } catch {
      setCopiedSection('');
    }
  };

  return (
    <div className="blog-root">
      <Header />
      <main className="blog-main blog-post-main">
        <nav className="blog-back-nav" aria-label="Back to blog list">
          <Link to="/blog">← Back to all posts</Link>
        </nav>

        <article className="blog-post">
          <header className="blog-post-header">
            <h1>{post.title}</h1>
            <p className="blog-post-meta">
              <span>{formatPublishedDate(post.publishedAt)}</span>
              <span aria-hidden="true">•</span>
              <span>{post.author}</span>
              <span aria-hidden="true">•</span>
              <span>{post.readTimeMinutes} min read</span>
            </p>
          </header>

          <img src={post.heroImage} alt={post.heroImageAlt} className="blog-post-hero-image" />

          <div className="blog-post-body">
            {post.sections.map((section) => (
              <section key={`${post.slug}-${section.heading}`} className="blog-post-section">
                <h2>{section.heading}</h2>
                {section.aiPrompt ? (
                  <div className="blog-ai-prompt">
                    <div className="blog-ai-prompt-toolbar">
                      <span>Prompt</span>
                      <button
                        type="button"
                        onClick={() => handleCopyPrompt(section)}
                        aria-label={`Copy ${section.heading} prompt`}
                      >
                        {copiedSection === section.heading ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <pre>{injectProfileUrl(section.aiPrompt, risqlyProfileUrl)}</pre>
                  </div>
                ) : (
                  section.paragraphs.map((paragraph, index) => (
                    <p key={`${post.slug}-${section.heading}-${index}`}>
                      {injectProfileUrl(paragraph, risqlyProfileUrl)}
                    </p>
                  ))
                )}
              </section>
            ))}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

export default BlogPost;
